import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MapPin, Eye, Copy, Plus, Trash2, Image as ImageIcon, Crown, TrendingUp, Bell, Download, Star, MessageSquare, QrCode, Instagram, Check } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { StoryShare } from "../components/StoryShare";
import { AnalyticsPanel } from "../components/AnalyticsPanel";
import { MarketsManager } from "../components/MarketsManager";
import { api, apiError, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CUISINES } from "../lib/cuisines";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const empty = { name: "", city: "", cuisine: "Burger", description: "", phone: "", instagram: "", facebook: "", website: "", photos: [], hours: {}, today_location: {}, schedule: [], loyalty_reward: "" };

export default function Dashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [truck, setTruck] = useState(undefined);
  const [form, setForm] = useState(empty);
  const [today, setToday] = useState({ label: "", address: "" });
  const [sched, setSched] = useState([]);
  const [subs, setSubs] = useState({ count: 0, subscribers: [], can_export: false });
  const [reviews, setReviews] = useState([]);
  const [replyDraft, setReplyDraft] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && !user) nav("/connexion"); }, [user, loading, nav]);
  useEffect(() => { if (user) api.get("/my/truck/subscribers").then((r) => setSubs(r.data)).catch(() => {}); }, [user]);
  useEffect(() => { if (truck) api.get("/my/reviews").then((r) => setReviews(r.data)).catch(() => {}); }, [truck]);
  useEffect(() => {
    if (!user) return;
    api.get("/my/truck").then((r) => {
      if (r.data) { setTruck(r.data); setForm({ ...empty, ...r.data }); setToday(r.data.today_location || { label: "", address: "" }); setSched(r.data.schedule || []); }
      else setTruck(null);
    });
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd); setForm((f) => ({ ...f, photos: [...(f.photos || []), data.url] })); toast.success("Photo ajoutée"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, cuisine: form.cuisine, schedule: sched };
      const r = truck ? await api.put("/my/truck", payload) : await api.post("/my/truck", payload);
      setTruck(r.data); toast.success(truck ? "Fiche mise à jour" : "Fiche créée et publiée !");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const saveToday = async () => {
    try { await api.post("/my/truck/today", today); toast.success("Emplacement du jour mis à jour"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const copyLink = () => { navigator.clipboard.writeText(`${window.location.origin}/foodtruck/${truck.slug}`); toast.success("Lien public copié !"); };
  const publicUrl = truck ? `${window.location.origin}/foodtruck/${truck.slug}` : "";
  const downloadQR = () => { const c = document.getElementById("truck-qr"); if (!c) return; const a = document.createElement("a"); a.href = c.toDataURL("image/png"); a.download = `${truck.slug}-qr.png`; a.click(); };
  const sendReply = async (id) => {
    try { await api.post(`/my/reviews/${id}/reply`, { reply: replyDraft[id] || "" }); toast.success("Réponse publiée"); const r = await api.get("/my/reviews"); setReviews(r.data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const exportSubs = () => {
    const csv = "email\n" + subs.subscribers.join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "abonnes-streateat.csv"; a.click(); URL.revokeObjectURL(url);
  };
  const upgrade = async (plan) => {
    try {
      const lookup = plan === "pro" ? "pro_launch" : "proplus_launch";
      const { data } = await api.post("/payments/checkout", { lookup_key: lookup, origin_url: window.location.origin, user_id: user.id });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); nav("/tarifs"); }
  };
  const isPro = user?.plan === "pro" || user?.plan === "proplus";
  const isProPlus = user?.plan === "proplus";
  const reviewCount = reviews.length;
  const avgRating = reviewCount ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviewCount).toFixed(1) : "-";
  const repliedCount = reviews.filter((r) => r.owner_reply).length;
  const replyRate = reviewCount ? Math.round((repliedCount / reviewCount) * 100) : 0;
  const maxPhotos = user?.plan === "proplus" ? 999 : user?.plan === "pro" ? 4 : 2;
  const maxSched = user?.plan === "free" ? 3 : 999;
  const checklist = truck ? [
    { label: "Photo ajoutée", done: (truck.photos || []).length > 0 },
    { label: "Description renseignée", done: !!truck.description },
    { label: "Type de cuisine choisi", done: !!truck.cuisine },
    { label: "Ville renseignée", done: !!truck.city },
    { label: "Planning de la semaine", done: (truck.schedule || []).length > 0 },
    { label: "Contact (tél. ou réseaux)", done: !!(truck.phone || truck.instagram || truck.website) },
  ] : [];
  const completion = checklist.length ? Math.round((checklist.filter((c) => c.done).length / checklist.length) * 100) : 0;

  if (loading || truck === undefined) return <p className="text-center py-24 text-urban-teal/50">Chargement...</p>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-display font-extrabold text-urban-teal">Mon espace pro</h1>
          <p className="text-urban-teal/60">Bonjour {user?.name} · Offre <span className="font-bold capitalize">{user?.plan}</span></p>
        </div>
        {truck && <Button onClick={copyLink} variant="outline" className="rounded-full border-urban-teal/20 text-urban-teal font-bold gap-2" data-testid="copy-link-btn"><Copy size={15} /> Copier mon lien public</Button>}
      </div>

      {truck && completion < 100 && (
        <div className="bg-white rounded-2xl shadow-warm p-6 mt-6" data-testid="onboarding-checklist">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold text-lg text-urban-teal">Complétez votre fiche</h2>
            <span className="font-display font-extrabold text-2xl text-street-gold" data-testid="completion-pct">{completion}%</span>
          </div>
          <div className="h-2 bg-cream-bg rounded-full mt-2 overflow-hidden"><div className="h-full bg-fresh-mint transition-all duration-500" style={{ width: `${completion}%` }} /></div>
          <p className="text-sm text-urban-teal/60 mt-2">Une fiche complète attire jusqu'à 3× plus de visites et gagne le badge « Vérifié ».</p>
          <ul className="grid sm:grid-cols-2 gap-2 mt-4">
            {checklist.map((c) => (
              <li key={c.label} className={`flex items-center gap-2 text-sm ${c.done ? "text-urban-teal/50 line-through" : "text-urban-teal font-semibold"}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-xs ${c.done ? "bg-fresh-mint text-white" : "border-2 border-cream-bg text-urban-teal/40"}`}>{c.done ? <Check size={13} /> : ""}</span>
                {c.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Stats */}
      {truck && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
          <div className="bg-white rounded-2xl shadow-warm p-5"><Eye className="text-street-gold" /><p className="font-display font-extrabold text-2xl text-urban-teal mt-2">{isPro ? truck.views : "-"}</p><p className="text-sm text-urban-teal/60">Vues de la fiche {!isPro && "(Pro)"}</p></div>
          <div className="bg-white rounded-2xl shadow-warm p-5"><Bell className="text-fresh-mint" /><p className="font-display font-extrabold text-2xl text-urban-teal mt-2">{isPro ? subs.count : "-"}</p><p className="text-sm text-urban-teal/60">Abonnés {!isPro && "(Pro)"}</p>{subs.can_export && subs.count > 0 && <button onClick={exportSubs} className="text-xs text-street-gold font-bold flex items-center gap-1 mt-1" data-testid="export-subs-btn"><Download size={12} /> Exporter (CSV)</button>}</div>
          <div className="bg-white rounded-2xl shadow-warm p-5"><TrendingUp className="text-coral-twist" /><p className="font-display font-extrabold text-2xl text-urban-teal mt-2 capitalize">{truck.status === "published" ? "En ligne" : truck.status}</p><p className="text-sm text-urban-teal/60">Statut</p></div>
          <div className="bg-white rounded-2xl shadow-warm p-5"><Crown className="text-street-gold" /><p className="font-display font-extrabold text-2xl text-urban-teal mt-2 capitalize">{user?.plan}</p><Link to="/tarifs" className="text-sm text-street-gold font-bold">Améliorer →</Link></div>
        </div>
      )}

      {/* Analytics réels (Phase 3) */}
      {truck && <AnalyticsPanel isPro={isPro} isProPlus={isProPlus} />}

      {/* Événements & marchés (Phase 5) */}
      {truck && <MarketsManager isPro={isPro} />}

      {/* Pro+ advanced stats */}
      {truck && isProPlus && (
        <div className="bg-white rounded-2xl shadow-warm p-6 mt-6" data-testid="advanced-stats">
          <h2 className="font-display font-bold text-lg text-urban-teal flex items-center gap-2 mb-1"><TrendingUp size={18} className="text-street-gold" /> Statistiques avancées <span className="text-[10px] bg-street-gold text-white px-2 py-0.5 rounded-full uppercase tracking-wide">Pro+</span></h2>
          <p className="text-sm text-urban-teal/60 mb-4">Vos indicateurs de performance détaillés, réservés à l'offre Pro+.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-cream-bg rounded-xl p-4" data-testid="adv-avg-rating"><Star className="text-street-gold" size={18} /><p className="font-display font-extrabold text-2xl text-urban-teal mt-1">{avgRating}</p><p className="text-xs text-urban-teal/60">Note moyenne</p></div>
            <div className="bg-cream-bg rounded-xl p-4" data-testid="adv-review-count"><MessageSquare className="text-fresh-mint" size={18} /><p className="font-display font-extrabold text-2xl text-urban-teal mt-1">{reviewCount}</p><p className="text-xs text-urban-teal/60">Avis reçus</p></div>
            <div className="bg-cream-bg rounded-xl p-4" data-testid="adv-reply-rate"><TrendingUp className="text-coral-twist" size={18} /><p className="font-display font-extrabold text-2xl text-urban-teal mt-1">{replyRate}%</p><p className="text-xs text-urban-teal/60">Taux de réponse</p></div>
            <div className="bg-cream-bg rounded-xl p-4" data-testid="adv-locations"><MapPin className="text-urban-teal" size={18} /><p className="font-display font-extrabold text-2xl text-urban-teal mt-1">{sched.length}</p><p className="text-xs text-urban-teal/60">Emplacements planifiés</p></div>
          </div>
        </div>
      )}

      {/* Today location */}
      {truck && (
        <div className="bg-urban-teal rounded-2xl p-6 mt-6 text-white">
          <p className="font-display font-bold text-lg flex items-center gap-2"><MapPin size={18} /> Emplacement du jour</p>
          <p className="text-white/60 text-sm">Déclarez où vous êtes aujourd'hui en quelques secondes.</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <input value={today.label} onChange={(e) => setToday({ ...today, label: e.target.value })} placeholder="Ex. Place Bellecour" className="rounded-xl px-3 py-2.5 text-urban-teal outline-none" data-testid="today-label" />
            <AddressAutocomplete value={today.address} onChange={(v) => setToday({ ...today, address: v })} onSelect={(s) => setToday({ ...today, address: s.label, lat: s.lat, lng: s.lng, label: today.label || s.city })} placeholder="Adresse (autocomplétée)" testid="today-address" Icon={MapPin} bias={form.city} />
          </div>
          <Button onClick={saveToday} className="rounded-full bg-street-gold hover:bg-street-gold/90 text-white font-bold mt-3" data-testid="save-today-btn">Mettre à jour</Button>
        </div>
      )}

      {user?.plan !== "proplus" && (
        <div className="bg-gradient-to-r from-street-gold to-coral-twist rounded-2xl p-5 mt-6 text-white flex flex-wrap items-center justify-between gap-3" data-testid="upgrade-banner">
          <div>
            <p className="font-display font-bold text-lg">{user?.plan === "free" ? "Passez en Pro et boostez votre visibilité" : "Passez en Pro+ et dominez votre zone"}</p>
            <p className="text-white/85 text-sm">{user?.plan === "free" ? "Mise en avant, planning illimité, stats, alertes abonnés, badge Vérifié, 4 photos." : "Position prioritaire, galerie illimitée, fidélité clients, Story Instagram, export abonnés."}</p>
          </div>
          <Button onClick={() => upgrade(user?.plan === "free" ? "pro" : "proplus")} className="rounded-full bg-white text-street-gold font-bold hover:bg-white/90 shrink-0" data-testid="upgrade-btn">{user?.plan === "free" ? "Passer en Pro" : "Passer en Pro+"}</Button>
        </div>
      )}

      {/* Edit form */}
      <div className="bg-white rounded-2xl shadow-warm p-6 mt-6">
        <h2 className="font-display font-bold text-xl text-urban-teal mb-4">{truck ? "Modifier ma fiche" : "Créer ma fiche foodtruck"}</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nom du foodtruck"><input value={form.name} onChange={set("name")} className="fld" data-testid="truck-name" /></Field>
          <Field label="Ville"><AddressAutocomplete value={form.city} onChange={(v) => setForm({ ...form, city: v })} onSelect={(s) => setForm({ ...form, city: s.city || s.label })} placeholder="Ville" testid="truck-city" Icon={MapPin} cityOnly /></Field>
          <Field label="Cuisine">
            <select value={form.cuisine} onChange={set("cuisine")} className="fld" data-testid="truck-cuisine">{CUISINES.map((c) => <option key={c}>{c}</option>)}</select>
          </Field>
          <Field label="Téléphone"><input value={form.phone} onChange={set("phone")} className="fld" data-testid="truck-phone" /></Field>
          <Field label="Instagram (pseudo)"><input value={form.instagram} onChange={set("instagram")} className="fld" /></Field>
          <Field label="Site web"><input value={form.website} onChange={set("website")} className="fld" /></Field>
          {isProPlus && <Field label="Récompense fidélité (10 tampons)"><input value={form.loyalty_reward} onChange={set("loyalty_reward")} placeholder="Ex. 1 boisson offerte" className="fld" data-testid="truck-reward" /></Field>}
        </div>
        <Field label="Description"><textarea value={form.description} onChange={set("description")} rows={3} className="fld" data-testid="truck-description" /></Field>

        <div className="mt-4">
          <label className="text-xs font-bold uppercase text-urban-teal/50">Galerie photo ({(form.photos || []).length}/{maxPhotos === 999 ? "∞" : maxPhotos})</label>
          <div className="flex flex-wrap gap-3 mt-2">
            {(form.photos || []).map((p, i) => (
              <div key={i} className="relative">
                <img src={mediaUrl(p)} alt="" className="w-20 h-20 object-cover rounded-xl" />
                <button onClick={() => setForm({ ...form, photos: form.photos.filter((_, j) => j !== i) })} className="absolute -top-2 -right-2 bg-coral-twist text-white rounded-full p-1"><Trash2 size={12} /></button>
              </div>
            ))}
            {(form.photos || []).length < maxPhotos ? (
              <label className="w-20 h-20 rounded-xl border-2 border-dashed border-urban-teal/30 flex items-center justify-center cursor-pointer text-urban-teal/40 hover:border-street-gold" data-testid="upload-photo">
                <ImageIcon size={22} /><input type="file" accept="image/*" onChange={upload} className="hidden" />
              </label>
            ) : (
              <div className="w-20 h-20 rounded-xl border-2 border-cream-bg flex items-center justify-center text-center text-[10px] text-urban-teal/40 p-1">Limite atteinte - <Link to="/tarifs" className="text-street-gold font-bold">upgrade</Link></div>
            )}
          </div>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold uppercase text-urban-teal/50">Planning multi-emplacements {user?.plan === "free" && "(3 max)"}</label>
            {sched.length < maxSched && <button onClick={() => setSched([...sched, { day: "", label: "", address: "" }])} className="text-street-gold font-bold text-sm flex items-center gap-1" data-testid="add-schedule"><Plus size={14} /> Ajouter</button>}
          </div>
          <div className="space-y-2 mt-2">
            {sched.map((s, i) => (
              <div key={i} className="grid grid-cols-3 gap-2">
                <input value={s.day} onChange={(e) => setSched(sched.map((x, j) => j === i ? { ...x, day: e.target.value } : x))} placeholder="Jour" className="fld" />
                <input value={s.label} onChange={(e) => setSched(sched.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="Lieu" className="fld" />
                <div className="flex gap-1 items-start"><div className="flex-1"><AddressAutocomplete value={s.address} onChange={(v) => setSched(sched.map((x, j) => j === i ? { ...x, address: v } : x))} onSelect={(sel) => setSched(sched.map((x, j) => j === i ? { ...x, address: sel.label } : x))} placeholder="Adresse" testid={`sched-address-${i}`} Icon={MapPin} bias={form.city} /></div><button onClick={() => setSched(sched.filter((_, j) => j !== i))} className="text-coral-twist px-1 pt-2"><Trash2 size={14} /></button></div>
              </div>
            ))}
            {user?.plan === "free" && <p className="text-xs text-urban-teal/40">Passez en <Link to="/tarifs" className="text-street-gold font-bold">Pro</Link> pour des tournées illimitées.</p>}
          </div>
        </div>

        <Button onClick={save} disabled={busy} className="rounded-full bg-street-gold hover:bg-street-gold/90 text-white font-bold mt-6 h-12 px-8" data-testid="save-truck-btn">{busy ? "Enregistrement..." : truck ? "Enregistrer" : "Créer et publier ma fiche"}</Button>
        {truck && <Link to={`/foodtruck/${truck.slug}`} className="ml-4 text-street-gold font-bold text-sm">Voir ma fiche publique →</Link>}
      </div>

      {truck && (
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <div className="bg-white rounded-2xl shadow-warm p-6" data-testid="bio-instagram">
            <h2 className="font-display font-bold text-xl text-urban-teal flex items-center gap-2 mb-1"><QrCode size={20} /> Lien & QR pour votre bio</h2>
            <p className="text-sm text-urban-teal/60 mb-4">Collez ce lien dans votre bio Instagram, ou affichez le QR code sur votre camion.</p>
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <div className="bg-cream-bg p-3 rounded-2xl"><QRCodeCanvas id="truck-qr" value={publicUrl} size={132} fgColor="#0E3F5B" bgColor="#FBF7EA" includeMargin /></div>
              <div className="flex-1 w-full">
                <div className="bg-cream-bg rounded-xl px-3 py-2 text-sm text-urban-teal break-all" data-testid="public-url">{publicUrl}</div>
                <div className="flex gap-2 mt-3">
                  <Button onClick={copyLink} className="rounded-full bg-urban-teal hover:bg-urban-teal/90 text-white font-bold gap-1" data-testid="bio-copy-btn"><Copy size={15} /> Copier</Button>
                  <Button onClick={downloadQR} variant="outline" className="rounded-full border-urban-teal/20 text-urban-teal font-bold gap-1" data-testid="download-qr-btn"><Download size={15} /> QR PNG</Button>
                </div>
              </div>
            </div>
          </div>

          {isProPlus && (
            <div className="bg-white rounded-2xl shadow-warm p-6" data-testid="story-card">
              <h2 className="font-display font-bold text-xl text-urban-teal flex items-center gap-2 mb-1"><Instagram size={20} className="text-coral-twist" /> Story Instagram</h2>
              <p className="text-sm text-urban-teal/60 mb-4">Générez une story de votre emplacement du jour, prête à poster.</p>
              <StoryShare truck={truck} />
            </div>
          )}
          <div className="bg-white rounded-2xl shadow-warm p-6" data-testid="manager-reviews">
            <h2 className="font-display font-bold text-xl text-urban-teal flex items-center gap-2 mb-3"><MessageSquare size={20} /> Avis clients ({reviews.length})</h2>
            {reviews.length === 0 && <p className="text-sm text-urban-teal/50">Aucun avis pour l'instant.</p>}
            <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
              {reviews.map((r) => (
                <div key={r.id} className="border-b border-cream-bg pb-3 last:border-0" data-testid={`dash-review-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-urban-teal">{r.name}</span>
                    <div className="flex">{[1,2,3,4,5].map((n) => <Star key={n} size={12} className={n <= r.rating ? "fill-street-gold text-street-gold" : "text-urban-teal/20"} />)}</div>
                  </div>
                  {r.comment && <p className="text-sm text-slate-600 mt-1">{r.comment}</p>}
                  {r.owner_reply ? (
                    <div className="bg-fresh-mint/10 rounded-xl p-2 mt-2 text-sm"><span className="font-bold text-fresh-mint">Votre réponse : </span><span className="text-urban-teal/80">{r.owner_reply}</span></div>
                  ) : (
                    <div className="flex gap-2 mt-2">
                      <input value={replyDraft[r.id] || ""} onChange={(e) => setReplyDraft({ ...replyDraft, [r.id]: e.target.value })} placeholder="Répondre publiquement..." className="flex-1 min-w-0 rounded-full border-2 border-cream-bg px-3 py-1.5 text-sm outline-none focus:border-urban-teal" data-testid={`reply-input-${r.id}`} />
                      <Button onClick={() => sendReply(r.id)} className="rounded-full bg-street-gold hover:bg-street-gold/90 text-white font-bold text-sm px-4 shrink-0" data-testid={`reply-btn-${r.id}`}>Répondre</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`.fld{width:100%;margin-top:4px;border:2px solid #FBF7EA;border-radius:12px;padding:10px 12px;outline:none;color:#0E3F5B}.fld:focus{border-color:#0E3F5B}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="mt-2"><label className="text-xs font-bold uppercase text-urban-teal/50">{label}</label>{children}</div>
);
